import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { INDUSTRIES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import CTASection from "@/components/CTASection";

export default function Industries() {
  useSEO({
    title: "Industries We Support",
    description:
      "Tier II and EPCRA compliance support for manufacturing, chemicals, warehousing, food & beverage, pharmaceuticals, laboratories, metal fabrication, plastics and energy facilities.",
    path: "/industries",
  });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Industries</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Compliance support tailored to your industry
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Different facilities store different hazards. We adapt our workflows to the chemicals and reporting realities
            of your sector.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.icon;
              return (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="group flex flex-col rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  data-testid={`industry-card-${ind.slug}`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h2 className="mt-4 font-heading text-xl font-bold text-navy-900">{ind.name}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{ind.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-navy-900 group-hover:text-amber-600 transition-colors">
                    Learn more <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <CTASection location="industries-footer" />
    </>
  );
}
